import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Badge, Modal,
    Form, Row, Col, Spinner,
} from 'react-bootstrap';
import { Plus, MapPin, Edit2, Trash2, Star } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const blank = {
    name: '', address: '', city: '', state: '',
    country: 'India', pincode: '', is_hq: false,
};

// ── Shared Confirm Modal (replaces window.confirm) ────────────────────────────
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm"/> : 'Delete'}
            </Button>
        </Modal.Footer>
    </Modal>
);

// ── Validation ────────────────────────────────────────────────────────────────
const validate = (form) => {
    const e = {};
    const name = form.name?.trim();
    if (!name)              e.name = 'Location name is required';
    else if (name.length < 2) e.name = 'Name must be at least 2 characters';
    else if (name.length > 100) e.name = 'Name cannot exceed 100 characters';

    if (form.address && form.address.length > 300)
        e.address = 'Address cannot exceed 300 characters';

    if (form.city && !/^[a-zA-Z\s\-'.]+$/.test(form.city.trim()))
        e.city = 'City should contain only letters';
    else if (form.city && form.city.trim().length > 60)
        e.city = 'City cannot exceed 60 characters';

    if (form.state && !/^[a-zA-Z\s\-'.]+$/.test(form.state.trim()))
        e.state = 'State should contain only letters';
    else if (form.state && form.state.trim().length > 60)
        e.state = 'State cannot exceed 60 characters';

    if (form.pincode && form.pincode.trim() !== '') {
        if (!/^\d{6}$/.test(form.pincode.trim()))
            e.pincode = 'Pincode must be exactly 6 digits';
    }

    if (!form.country?.trim())
        e.country = 'Country is required';
    else if (form.country.trim().length > 60)
        e.country = 'Country cannot exceed 60 characters';

    return e;
};

// ── Main Component ────────────────────────────────────────────────────────────
const LocationsPage = () => {
    const [locations, setLocations] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [modal,     setModal]     = useState({ show: false, editing: null });
    const [form,      setForm]      = useState(blank);
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [confirm,   setConfirm]   = useState({ show: false, id: null, name: '', loading: false });

    const fetchLocations = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/hr/locations');
            setLocations(res.data.data || []);
        } catch { toast.error('Failed to load locations'); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchLocations(); }, []);

    const openCreate = () => {
        setModal({ show: true, editing: null });
        setForm(blank);
        setErrors({});
        setAttempted(false);
    };

    const openEdit = (l) => {
        setModal({ show: true, editing: l });
        setForm({ ...l, is_hq: !!l.is_hq });
        setErrors({});
        setAttempted(false);
    };

    const closeModal = () => {
        setModal(m => ({ ...m, show: false }));
        setErrors({});
        setAttempted(false);
    };

    // Update field and clear its error in real time
    const setField = (field, value) => {
        setForm(p => ({ ...p, [field]: value }));
        if (attempted) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handleSave = async () => {
        setAttempted(true);
        const errs = validate(form);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setSaving(true);
        try {
            if (modal.editing) {
                await axiosInstance.put(`/hr/locations/${modal.editing.id}`, form);
                toast.success('Location updated');
            } else {
                await axiosInstance.post('/hr/locations', form);
                toast.success('Location added');
            }
            closeModal();
            fetchLocations();
        } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    const handleDeleteConfirm = async () => {
        setConfirm(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.delete(`/hr/locations/${confirm.id}`);
            toast.success('Location deleted');
            fetchLocations();
        } catch { toast.error('Delete failed'); }
        finally { setConfirm({ show: false, id: null, name: '', loading: false }); }
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">
                        <MapPin size={20} className="me-2 text-primary"/>Office Locations
                    </h4>
                    <p className="text-muted small mb-0">Manage your company's office locations and branches</p>
                </div>
                <Button variant="primary" className="d-flex align-items-center gap-2" onClick={openCreate}>
                    <Plus size={18}/>Add Location
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
                ) : (
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Location Name</th>
                                <th>City</th>
                                <th>State</th>
                                <th>Country</th>
                                <th>Pincode</th>
                                <th>HQ</th>
                                <th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {locations.length > 0 ? locations.map(loc => (
                                <tr key={loc.id}>
                                    <td className="ps-4 fw-medium">
                                        <MapPin size={14} className="me-1 text-muted"/>{loc.name}
                                    </td>
                                    <td>{loc.city  || '—'}</td>
                                    <td>{loc.state || '—'}</td>
                                    <td>{loc.country}</td>
                                    <td>{loc.pincode || '—'}</td>
                                    <td>
                                        {loc.is_hq
                                            ? <Badge bg="warning" text="dark"><Star size={11} className="me-1"/>HQ</Badge>
                                            : '—'}
                                    </td>
                                    <td className="text-end pe-4">
                                        <Button variant="link" size="sm" className="text-muted p-1"
                                            onClick={() => openEdit(loc)}>
                                            <Edit2 size={15}/>
                                        </Button>
                                        <Button variant="link" size="sm" className="text-danger p-1"
                                            onClick={() => setConfirm({ show: true, id: loc.id, name: loc.name, loading: false })}>
                                            <Trash2 size={15}/>
                                        </Button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="text-center py-5 text-muted">
                                        No locations added yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Card>

            {/* ── Add / Edit Modal ──────────────────────────────────────── */}
            <Modal show={modal.show} onHide={closeModal} centered size="lg">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">
                        {modal.editing ? 'Edit Location' : 'Add Office Location'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Location Name *</Form.Label>
                            <Form.Control
                                placeholder="e.g. Tirupati HQ"
                                value={form.name}
                                isInvalid={attempted && !!errors.name}
                                onChange={e => setField('name', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                        </Col>

                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Address</Form.Label>
                            <Form.Control as="textarea" rows={2}
                                placeholder="Street address"
                                value={form.address}
                                isInvalid={attempted && !!errors.address}
                                onChange={e => setField('address', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                        </Col>

                        <Col xs={12} sm={4}>
                            <Form.Label className="small fw-bold">City</Form.Label>
                            <Form.Control
                                placeholder="Tirupati"
                                value={form.city}
                                isInvalid={attempted && !!errors.city}
                                onChange={e => setField('city', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
                        </Col>

                        <Col xs={12} sm={4}>
                            <Form.Label className="small fw-bold">State</Form.Label>
                            <Form.Control
                                placeholder="Andhra Pradesh"
                                value={form.state}
                                isInvalid={attempted && !!errors.state}
                                onChange={e => setField('state', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
                        </Col>

                        <Col xs={12} sm={4}>
                            <Form.Label className="small fw-bold">Pincode</Form.Label>
                            <Form.Control
                                placeholder="517501"
                                value={form.pincode}
                                maxLength={6}
                                isInvalid={attempted && !!errors.pincode}
                                onChange={e => setField('pincode', e.target.value.replace(/\D/g, ''))}/>
                            <Form.Control.Feedback type="invalid">{errors.pincode}</Form.Control.Feedback>
                        </Col>

                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Country *</Form.Label>
                            <Form.Control
                                value={form.country}
                                isInvalid={attempted && !!errors.country}
                                onChange={e => setField('country', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
                        </Col>

                        <Col xs={12} sm={6} className="d-flex align-items-end pb-1">
                            <Form.Check
                                type="switch"
                                id="is-hq"
                                label="This is the Headquarters (HQ)"
                                checked={form.is_hq}
                                onChange={e => setField('is_hq', e.target.checked)}/>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={closeModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : modal.editing ? 'Save Changes' : 'Add Location'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Delete Confirm Modal ──────────────────────────────────── */}
            <ConfirmModal
                show={confirm.show}
                title="Delete Location"
                message={`Delete "${confirm.name}"? This cannot be undone.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirm({ show: false, id: null, name: '', loading: false })}
                loading={confirm.loading}/>
        </div>
    );
};

export default LocationsPage;
